import { merge } from 'lodash-es';
import { isValidElement, type PropsWithChildren, type ReactNode } from 'react';

import { IntentProps } from '../../../core/slots/Intent/Intent.js';
import { SingleValueAlignment } from '../../core/components/single-value-renderer/types/single-value-base-props.js';
import { getToolbarConfigWithoutIntents } from '../../core/components/toolbar/utils/get-toolbar-config-without-intents.js';
import { isValidColorRule } from '../../core/slots/ColorRule.js';
import { defaultSingleValueConfig } from '../defaults.js';
import {
  defaultToolbarInternalConfig,
  type SingleValueToolbarInternalProps,
  type SingleValueToolbarSlotProps,
} from '../types/single-value-toolbar.js';
import type {
  SingleValueConfigProperties,
  SingleValueProps,
  SingleValueSparklineConfig,
} from '../types/single-value.js';
import type { SingleValueSlots } from './iterate-slots.js';

function getNodeInnerText(node: ReactNode): string {
  // String literals
  if (typeof node === 'string') {
    return node;
  }

  // Empty
  if (
    node === null ||
    typeof node === 'boolean' ||
    typeof node === 'undefined'
  ) {
    return '';
  }

  // Numeric children
  if (typeof node === 'number') {
    return node.toString();
  }

  // Children prop
  if (
    isValidElement<PropsWithChildren>(node) &&
    node.props &&
    node.props.children
  ) {
    return getNodeInnerText(node.props.children);
  }

  // Default
  return '';
}

/**
 * Deserializes a SingleValue string config
 *
 * @param serializedConfig - string
 * @returns SingleValueConfigProperties
 */
export function deserializeConfig(
  serializedConfig: string,
): SingleValueConfigProperties {
  try {
    return merge({}, defaultSingleValueConfig, JSON.parse(serializedConfig));
  } catch {
    return defaultSingleValueConfig;
  }
}

/**
 * Checks if the contextConfig is a serialized-string configuration
 * @param contextConfig - context configuration.
 * @returns true if the received contextConfig is a string.
 */
export function isSerializedConfig(
  contextConfig: SingleValueConfigProperties | string,
): contextConfig is string {
  return typeof contextConfig === 'string';
}

/**
 * Normalizes a SingleValue config to look like the configuration object
 * expected by the SingleValueConfig.
 *
 * @param config - config from context
 * @returns SingleValueConfigProperties
 */
export function normalizeConfig(
  config: SingleValueConfigProperties,
): SingleValueConfigProperties {
  const sparklineConfig = config.sparkline;
  const toolbar = config.toolbar
    ? getToolbarConfigWithoutIntents(config.toolbar)
    : undefined;

  const output = {
    ...config,
    label: getNodeInnerText(config.label),
    toolbar,
  };
  if (sparklineConfig) {
    output.sparkline = sparklineConfig;
  }
  return output;
}

export const buildSparklineConfig = (
  slotsSparklineConfig?: SingleValueSparklineConfig,
  contextSparklineConfig?: SingleValueSparklineConfig,
): SingleValueSparklineConfig | undefined => {
  if (!slotsSparklineConfig) {
    return;
  }

  const contextThresholds = contextSparklineConfig?.thresholdIndicators || [];
  const slotThresholds = slotsSparklineConfig?.thresholdIndicators || [];

  return {
    thresholdIndicators: contextThresholds.concat(slotThresholds),
    color: slotsSparklineConfig?.color ?? contextSparklineConfig?.color,
    variant: slotsSparklineConfig?.variant ?? contextSparklineConfig?.variant,
    xAxis: {
      ...contextSparklineConfig?.xAxis,
      ...slotsSparklineConfig?.xAxis,
    },
    yAxis: {
      ...contextSparklineConfig?.yAxis,
      ...slotsSparklineConfig?.yAxis,
    },
    showTicks:
      slotsSparklineConfig?.showTicks ?? contextSparklineConfig?.showTicks,
    showContextValues:
      slotsSparklineConfig?.showContextValues ??
      contextSparklineConfig?.showContextValues,
    gapPolicy:
      slotsSparklineConfig?.gapPolicy ?? contextSparklineConfig?.gapPolicy,
    curve: slotsSparklineConfig?.curve,
  };
};

const getDefaultToolbarPlacement = (
  singleValueAlignment: SingleValueAlignment | undefined,
) => {
  return singleValueAlignment === 'end'
    ? 'top-left'
    : defaultToolbarInternalConfig.placement;
};
const getToolbarConfig = (
  toolbarSlotsConfig?: SingleValueToolbarSlotProps & {
    intents?: IntentProps[];
  },
  toolbarConsumerConfig?: SingleValueToolbarInternalProps,
  singleValueAlignment?: SingleValueAlignment,
) => {
  if (!toolbarSlotsConfig && !toolbarConsumerConfig) {
    return undefined;
  }

  const placement =
    toolbarSlotsConfig?.placement ??
    toolbarConsumerConfig?.placement ??
    getDefaultToolbarPlacement(singleValueAlignment);

  return merge(
    {},
    defaultToolbarInternalConfig,
    toolbarConsumerConfig,
    toolbarSlotsConfig,
    { placement },
  );
};

/**
 * Build SingleValue configuration.
 *
 * @param props - config from props
 * @param slots - config from slot components
 * @param contextConfig - config from the context
 * @returns SingleValueInternalConfig
 */
export function buildSingleValueConfig(
  props: Omit<SingleValueProps, 'data' | 'prefixIcon'>,
  slots: SingleValueSlots,
  contextConfig: SingleValueConfigProperties,
): SingleValueConfigProperties {
  const colorRules = (contextConfig.colorRules || [])
    .filter(isValidColorRule)
    .concat(slots.colorRules || []);

  const toolbar = getToolbarConfig(
    slots.toolbar,
    contextConfig.toolbar,
    props.alignment,
  );

  return {
    label: props.label ?? contextConfig.label,
    color: props.color ?? contextConfig.color,
    unit: props.unit ?? contextConfig.unit ?? defaultSingleValueConfig.unit,
    formatter: props.formatter ?? contextConfig.formatter,
    alignment:
      props.alignment ??
      contextConfig.alignment ??
      defaultSingleValueConfig.alignment,
    sparkline: buildSparklineConfig(slots.sparkline, contextConfig.sparkline),
    colorRules,
    toolbar,
  };
}
